'use client';

import { useEffect, useRef } from 'react';

import { usePathname } from 'next/navigation';

import { COOKIEYES_SHOW_BTN_CLASS, PATHS_TO_SHOW_COOKIEYES_BUTTON } from '@/utils/constants';
import { GeneralPath } from '@/utils/models/page-paths';

type AnchorWindow = Window & { __anchorHash?: string };

/** The document-level effects of `pages/_app.tsx` (no UI). */
export const AppShellEffects = () => {
  const pathname = usePathname() ?? '';
  const previousPathnameRef = useRef<string | null>(null);

  // Anchor deep links (/#ai-quiz): the inline script in the root layout stripped the hash before
  // the browser could hard-jump and stashed it on window. Once mounted, glide down to the
  // target instead — the same script disabled entrance reveals, so the scroll travels
  // through fully visible content — then put the hash back in the URL.
  useEffect(() => {
    const anchorWindow = window as AnchorWindow;
    const anchorHash = anchorWindow.__anchorHash;
    if (!anchorHash) return;
    delete anchorWindow.__anchorHash;
    const target = document.getElementById(anchorHash.slice(1));
    if (!target) return;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    window.setTimeout(() => {
      target.scrollIntoView({ behavior });
      history.replaceState(null, '', anchorHash);
    }, 150);
  }, []);

  // _app's routeChangeComplete handler: keep the anchor-jump reveal-disable flag in sync on
  // client-side navigation (a hash target needs everything visible instantly, a plain route gets
  // its entrance animations back). Skipped on the first render — the pre-paint script owns it then.
  // (App Router scrolls to the top on navigation itself, so _app's routeChangeStart scrollTo is gone.)
  useEffect(() => {
    if (previousPathnameRef.current === null) {
      previousPathnameRef.current = pathname;
      return;
    }
    if (previousPathnameRef.current === pathname) return;
    previousPathnameRef.current = pathname;
    if (window.location.hash) document.documentElement.setAttribute('data-anchor-jump', '');
    else document.documentElement.removeAttribute('data-anchor-jump');
  }, [pathname]);

  // adding class only to these pages, where cookieYes cookie button should be shown
  useEffect(() => {
    if (PATHS_TO_SHOW_COOKIEYES_BUTTON.includes(pathname as GeneralPath)) {
      document.body.classList.add(COOKIEYES_SHOW_BTN_CLASS);
    } else {
      document.body.classList.remove(COOKIEYES_SHOW_BTN_CLASS);
    }
  }, [pathname]);

  return null;
};
